import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import { isPhoneViewport } from '../../../core/utils/phone';

/** One result's site, as the result views hand it in. */
export interface SiteEntry {
  host: string;
  favicon: string | null;
}

interface SiteView extends SiteEntry {
  count: number;
}

/** Lists this many until "Show all". */
const TOP = 6;

let nextId = 0;

/**
 * "Top sites" for the Playground's result views: the domains with the most
 * results, each a toggle, so the results can be filtered to one or more
 * sites. A list in the side column on a wide panel, a row of chips above
 * the results on a narrow one (the container is the result view's host).
 * The filter itself lives in the result view; this only shows and toggles it.
 */
@Component({
  selector: 'app-top-sites',
  standalone: false,
  templateUrl: './top-sites.html',
  styleUrl: './top-sites.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopSites {
  /** One entry per result (passage, article…); counted here per host. */
  readonly entries = input.required<readonly SiteEntry[]>();
  readonly selected = input.required<ReadonlySet<string>>();
  readonly toggle = output<string>();
  readonly clear = output<void>();

  protected readonly showAll = signal(false);
  protected readonly titleId = `top-sites-title-${nextId++}`;

  private readonly all = computed<SiteView[]>(() => {
    const byHost = new Map<string, SiteView>();
    for (const e of this.entries()) {
      const existing = byHost.get(e.host);
      if (existing) {
        existing.count++;
        existing.favicon ??= e.favicon;
      } else {
        byHost.set(e.host, { host: e.host, favicon: e.favicon, count: 1 });
      }
    }
    return [...byHost.values()].sort((a, b) => b.count - a.count);
  });

  /** Phones show every site in one sideways-scrolling row (top-sites.scss),
   *  so there's no cut and no "Show all". */
  protected readonly phone = isPhoneViewport();

  /** The first few, or all after "Show all". A picked site stays listed
   *  even past the cut, so it can be unpicked. */
  protected readonly sites = computed<SiteView[]>(() => {
    const all = this.all();
    if (this.showAll() || this.phone) return all;
    const picked = this.selected();
    return all.filter((site, i) => i < TOP || picked.has(site.host));
  });

  protected readonly total = computed(() => this.all().length);

  /** A favicon that 404s shouldn't leave a broken-image icon. */
  protected hideBrokenImage(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}
